import React, { useEffect, useState } from 'react';
import { 
  Users, Plus, Trash2, Shield, AlertTriangle, Search, 
  Grid3x3, List, Table as TableIcon, Mail, Phone, CheckCircle2, XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clientMembersService from '@/services/clientMembersService';
import { getMyProfile } from '@/services/clientService';
import type { ClientMember } from '@/types/client';
import { MemberRoleBadge } from '@/components/clients';
import { ClientMemberModal } from '@/components/clients/ClientMemberModal';
import { useConfirm } from '@/components/ConfirmDialog';
import { AddButton } from '@/components/ui/AddButton';
import { useAlert } from '@/components/AlertProvider';

const BuyerMembers: React.FC = () => {
  const navigate = useNavigate();
  const { showConfirm } = useConfirm();
  const { showSuccess, showError } = useAlert();
  const [members, setMembers] = useState<ClientMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientId, setBuyerId] = useState<number | null>(null);
  const [memberType, setMemberType] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ClientMember | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>('table');
  const [search, setSearch] = useState('');

  const filteredMembers = members.filter(member => {
    const s = search.toLowerCase();
    return (
      member.first_name.toLowerCase().includes(s) ||
      member.last_name.toLowerCase().includes(s) ||
      member.email.toLowerCase().includes(s) ||
      (member.phone && member.phone.toLowerCase().includes(s))
    );
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [membersData, profileData] = await Promise.all([
        clientMembersService.list(),
        getMyProfile()
      ]);
      
      // 🔍 LOGS DE DIAGNOSTIC
      console.log('=== Client MEMBERS - DIAGNOSTIC ===');
      console.log('Profile Data:', profileData);
      console.log('member_type:', profileData.member_type);
      console.log('client_type:', profileData.client_type);
      console.log('email:', profileData.email);
      console.log('Has member_type?', !!profileData.member_type);
      console.log('Access should be denied?', !!profileData.member_type);
      console.log('=================================');
      
      // Vérifier si l'utilisateur a le droit d'accéder à cette page
      // SEULS les buyers (clients directs) peuvent gérer les contacts
      // Aucun contact (même gérant) ne peut accéder à cette page
      if (profileData.member_type) {
        console.log('🚫 ACCESS DENIED - User is a member, not a Client');
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      console.log('✅ ACCESS GRANTED - User is a Client');
      setMembers(membersData);
      setBuyerId(profileData.id);
      setMemberType(profileData.member_type || null);
    } catch (error: any) {
      console.error('❌ Error loading data:', error);
      
      // Si erreur 403, c'est un problème de permission (ex: Agent qui essaie d'accéder)
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError('Impossible de charger les données. Veuillez réessayer.');
        showError('Erreur chargement données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (member?: ClientMember) => {
    // Vérifier que clientId est chargé avant d'ouvrir la modal
    if (!clientId && !member) {
      showError('Profil non chargé, veuillez patienter...');
      return;
    }
    setSelectedMember(member || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMember(null);
  };

  const handleSuccess = () => {
    loadData();
  };

  const handleDelete = async (member: ClientMember) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le contact',
      message: `Êtes-vous sûr de vouloir supprimer ${member.first_name} ${member.last_name} ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });
    
    if (!confirmed) return;
    
    try {
      await clientMembersService.delete(member.id);
      showSuccess('contact supprimé avec succès');
      loadData();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Erreur suppression contact');
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 border-2 border-red-200 dark:border-red-800">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                Erreur
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {error}
              </p>
              <button
                onClick={() => loadData()}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 border-2 border-red-200 dark:border-red-800">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                Accès Refusé
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Vous n'avez pas les permissions nécessaires pour accéder à cette page. 
                Seuls les clients (buyers) peuvent gérer les utilisateurs.
              </p>
              <button
                onClick={() => navigate('/client/dashboard')}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
              >
                Retour au Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950 overflow-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg sticky top-0 z-10">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Gestion des utilisateurs</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              Gérez les utilisateurs de votre organisation
            </p>
          </div>
        </div>
        {!memberType && (
          <AddButton
            onClick={() => handleOpenModal()}
            label="Nouveau"
            disabled={loading || !clientId}
            className="w-full sm:w-auto"
          />
        )}
      </div>

      {/* Search and View Switcher */}
      <div className="px-6 py-4 flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un contact..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1 bg-white dark:bg-slate-800">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'grid'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Grille"
          >
            <Grid3x3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'list'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Liste"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'table'
              ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
              : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            title="Vue Tableau"
          >
            <TableIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {filteredMembers.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-700">
            <Users className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {members.length === 0 ? 'Aucun contact' : 'Aucun résultat'}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              {members.length === 0 
                ? 'Commencez par créer votre premier contact' 
                : 'Aucun contact ne correspond à votre recherche'}
            </p>
            {!memberType && members.length === 0 && (
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-200"
              >
                <Plus className="h-5 w-5" />
                <span>Créer un contact</span>
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredMembers.map((member) => (
              <div
                key={member.id}
                onClick={() => handleOpenModal(member)}
                className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {member.first_name[0]}{member.last_name[0]}
                  </div>
                  {!memberType && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(member);
                      }}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="mb-4">
                  <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate text-lg mb-3">
                    {member.first_name} {member.last_name}
                  </h3>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Mail className={`h-3.5 w-3.5 ${member.email ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                      </div>
                      <span className={`truncate ${member.email ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                        {member.email || 'Email non renseigné'}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Phone className={`h-3.5 w-3.5 ${member.phone ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                      </div>
                      <span className={`truncate ${member.phone ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                        {member.phone || 'Téléphone non renseigné'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-100 dark:border-slate-700">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-500 uppercase">Rôle</span>
                    <MemberRoleBadge role={member.member_type} />
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-500 uppercase">Accès</span>
                    {member.access_all_sales_points ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full font-medium">
                        <Shield className="h-3 w-3" />
                        Tout
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded-full font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        Restreint ({member.sales_point_ids.length})
                      </span>
                    )}
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-500 uppercase">Statut</span>
                    {member.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                        <CheckCircle2 className="h-3 w-3" /> Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                        <XCircle className="h-3 w-3" /> Inactif
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-3">
            {filteredMembers.map((member) => (
              <div
                key={member.id}
                onClick={() => handleOpenModal(member)}
                className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-4 hover:shadow-lg hover:border-cyan-300 dark:hover:border-cyan-700 hover:translate-x-1 transition-all duration-300 cursor-pointer flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md flex-shrink-0">
                  {member.first_name[0]}{member.last_name[0]}
                </div>

                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate mb-1">
                      {member.first_name} {member.last_name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Mail className={`h-3 w-3 ${member.email ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                      </div>
                      <span className={`truncate text-xs ${member.email ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                        {member.email || 'Email non renseigné'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-12">Rôle:</span>
                      <MemberRoleBadge role={member.member_type} />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-12">Statut:</span>
                      {member.is_active ? (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Actif
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Inactif
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Accès:</span>
                    {member.access_all_sales_points ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full font-medium">
                        <Shield className="h-3 w-3" />
                        Tous les points
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded-full font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        {member.sales_point_ids.length} point(s)
                      </span>
                    )}
                  </div>

                  <div className="hidden lg:block">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Phone className={`h-3 w-3 ${member.phone ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                      </div>
                      <span className={`truncate text-xs ${member.phone ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                        {member.phone || 'Non renseigné'}
                      </span>
                    </div>
                  </div>
                </div>

                {!memberType && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(member);
                    }}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : viewMode === 'table' ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Accès
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredMembers.map((member) => (
                  <tr 
                    key={member.id} 
                    onClick={() => handleOpenModal(member)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                          {member.first_name[0]}{member.last_name[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {member.first_name} {member.last_name}
                          </div>
                          <div className="hidden lg:block">
                            <div className="flex items-center gap-2 text-sm">
                              <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                                <Phone className={`h-3 w-3 ${member.phone ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`} />
                              </div>
                              <span className={`truncate text-xs ${member.phone ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400 italic'}`}>
                                {member.phone || 'Non renseigné'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                      {member.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <MemberRoleBadge role={member.member_type} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.access_all_sales_points ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                          <Shield className="h-3 w-3" />
                          Tous les points
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {member.sales_point_ids.length} point(s)
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.is_active ? (
                        <span className="inline-flex px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!memberType && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(member);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {/* Modal */}
      <ClientMemberModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        member={selectedMember}
        clientId={clientId || 0}
      />
    </div>
  );
};

export default BuyerMembers;
